// ============================================================
//  Vertragsgrundlage je Klasse gegen heute: Wie viele Personen je Lizenzklasse hat der
//  Vertrag angenommen, wie viele zählen heute unter dem aktiven Identitätsschlüssel, und
//  was macht die Differenz in FUE aus? Die Summenzeile ist das Delta des Cockpits
//  (Bedarf − Vertragszahl) — dieselbe Geldlogik, nur je Klasse aufgeschlüsselt.
// ============================================================
import { CONTRACT_CLASSES, type ContractBasis, type ContractClass } from "../store/contract";
import { DEFAULT_RATIO, type RatioTable, weight } from "./rules";
import type { IdentityResolution } from "./types";

export interface ContractCompareRow {
  cls: ContractClass;
  /** Gewicht der Klasse (FUE je Person) aus der Verhältnistabelle. */
  weight: number;
  /** Vertraglich angenommene Personen und ihr FUE-Beitrag. */
  assumed: number;
  assumedFue: number;
  /** Heute unter dem aktiven Schlüssel erkannte Personen und ihr FUE-Beitrag. */
  today: number;
  todayFue: number;
  /** heute − angenommen (positiv = mehr als der Vertrag annimmt). */
  diffPersons: number;
  diffFue: number;
}

export interface ContractComparison {
  rows: ContractCompareRow[];
  assumed: number;
  assumedFue: number;
  today: number;
  todayFue: number;
  diffPersons: number;
  /** Ungerundete FUE-Differenz (Σ heute − Σ angenommen). */
  diffFue: number;
  /** Gerundet wie im Cockpit: round(Bedarf) − round(Vertragszahl) = kpis.delta. */
  deltaCockpit: number;
}

/**
 * Vergleich der Vertragsgrundlage mit den heute erkannten Personen je Klasse.
 * `byClass` kommt aus resolveIdentity (aktiver Schlüssel); „nicht relevant" bleibt außen vor,
 * weil der Vertrag dafür keine Position hat und das Gewicht 0 ist.
 */
export function contractComparison(basis: ContractBasis, byClass: IdentityResolution["byClass"], ratio: RatioTable = DEFAULT_RATIO): ContractComparison {
  const rows: ContractCompareRow[] = CONTRACT_CLASSES.map((cls) => {
    const w = weight(cls, ratio);
    const assumed = basis[cls];
    const today = byClass[cls]?.persons ?? 0;
    const todayFue = byClass[cls]?.fue ?? today * w;
    return { cls, weight: w, assumed, assumedFue: assumed * w, today, todayFue, diffPersons: today - assumed, diffFue: todayFue - assumed * w };
  });
  const sum = (f: (r: ContractCompareRow) => number) => rows.reduce((s, r) => s + f(r), 0);
  const assumedFue = sum((r) => r.assumedFue);
  const todayFue = sum((r) => r.todayFue);
  return {
    rows,
    assumed: sum((r) => r.assumed), assumedFue,
    today: sum((r) => r.today), todayFue,
    diffPersons: sum((r) => r.diffPersons), diffFue: todayFue - assumedFue,
    deltaCockpit: Math.round(todayFue) - Math.round(assumedFue),
  };
}
